import React, { useState, useMemo } from 'react';
import { formatMetricName, formatDateLabel, isDateValue, inferFormat, formatValue } from '../../charts/chartUtils';

/**
 * Analiza las columnas para determinar tipo y formato.
 * Retorna un mapa: { columnName: { type: 'date'|'numeric'|'string', format: 'currency'|'percentage'|'number'|null } }
 */
function analyzeColumns(columns, rows) {
    const analysis = {};
    for (const col of columns) {
        const firstValue = rows.find(r => r[col] != null)?.[col];
        if (firstValue !== undefined && isDateValue(String(firstValue))) {
            analysis[col] = { type: 'date', format: null };
        } else if (typeof firstValue === 'number') {
            analysis[col] = { type: 'numeric', format: inferFormat(col) };
        } else {
            analysis[col] = { type: 'string', format: null };
        }
    }
    return analysis;
}

function formatCell(value, colInfo) {
    if (value === null || value === undefined) return '-';
    if (colInfo.type === 'date') return formatDateLabel(String(value));
    if (colInfo.type === 'numeric') return formatValue(value, colInfo.format);
    return value;
}

export function TableBlock({ data }) {
    const rows = data?.rows || [];
    const columnLabels = data?.column_labels || {};
    const [sortConfig, setSortConfig] = useState({ column: null, direction: 'desc' });

    const { columns, colAnalysis } = useMemo(() => {
        const allCols = Object.keys(rows[0] || {});
        const analysis = analyzeColumns(allCols, rows);
        const dims = allCols.filter(c => analysis[c].type !== 'numeric');
        const metrics = allCols.filter(c => analysis[c].type === 'numeric');
        return { columns: [...dims, ...metrics], colAnalysis: analysis };
    }, [rows]);

    const sortedRows = useMemo(() => {
        if (!sortConfig.column) return rows;
        const col = sortConfig.column;
        const dir = sortConfig.direction === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const va = a[col];
            const vb = b[col];
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
            return String(va).localeCompare(String(vb)) * dir;
        });
    }, [rows, sortConfig]);

    if (rows.length === 0) {
        return <p className="no-data">Sin datos para mostrar</p>;
    }

    const handleSort = (col) => {
        setSortConfig(prev => {
            if (prev.column === col) {
                return { column: col, direction: prev.direction === 'desc' ? 'asc' : 'desc' };
            }
            return { column: col, direction: 'desc' };
        });
    };

    return (
        <div className="table-block">
            <div className="table-responsive">
                <table className="report-table">
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col}
                                    className="sortable-header"
                                    onClick={() => handleSort(col)}
                                >
                                    {columnLabels[col] || formatMetricName(col)}
                                    {sortConfig.column === col && (
                                        <span className="sort-indicator">
                                            {sortConfig.direction === 'asc' ? ' \u25B2' : ' \u25BC'}
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {columns.map((col) => (
                                    <td
                                        key={col}
                                        className={colAnalysis[col].type === 'numeric' ? 'numeric-cell' : undefined}
                                    >
                                        {formatCell(row[col], colAnalysis[col])}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
